class DetailsModal extends HTMLElement {
  constructor() {
    super();
    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.headerWrapper = this.closest('.header-wrapper');

    this.detailsContainer.addEventListener('keyup', (event) => event.code.toUpperCase() === 'ESCAPE' && this.close());
    this.summaryToggle.addEventListener('click', this.onSummaryClick.bind(this));
    this.querySelector('button[type="button"]').addEventListener('click', this.close.bind(this));

    this.summaryToggle.setAttribute('role', 'button');
    this.summaryToggle.setAttribute('aria-expanded', 'false');
  }

  isOpen() {
    return this.detailsContainer.hasAttribute('open');
  }

  isMobileHeaderSearch() {
    return this.classList.contains('header__search--mobile') && window.matchMedia('(max-width: 989px)').matches;
  }

  updateMobileSearchViewport() {
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    document.documentElement.style.setProperty('--sc-search-viewport-height', `${viewportHeight}px`);
  }

  getMobileSearchInput() {
    return this.querySelector('.search__input');
  }

  startMobileSearchPlaceholderAnimation() {
    const input = this.getMobileSearchInput();
    const placeholders = this.dataset.searchPlaceholders?.split('|').filter(Boolean) || [];

    if (!input || placeholders.length < 2) return;

    this.stopMobileSearchPlaceholderAnimation();
    this.mobileSearchPlaceholders = placeholders;
    this.mobileSearchPlaceholderIndex = placeholders.indexOf(input.getAttribute('placeholder'));
    if (this.mobileSearchPlaceholderIndex < 0) this.mobileSearchPlaceholderIndex = 0;
    input.setAttribute('placeholder', '');
    this.runMobileSearchPlaceholderTypewriter();
  }

  runMobileSearchPlaceholderTypewriter() {
    const input = this.getMobileSearchInput();

    if (!input || !this.mobileSearchPlaceholders?.length) return;
    if (!this.detailsContainer.hasAttribute('open')) return;
    if (input.value.trim()) {
      input.setAttribute('placeholder', '');
      return;
    }

    const phrase = this.mobileSearchPlaceholders[this.mobileSearchPlaceholderIndex] || '';
    let characterIndex = 0;
    let isDeleting = false;

    const tick = () => {
      if (!this.detailsContainer.hasAttribute('open')) return;

      if (input.value.trim()) {
        input.setAttribute('placeholder', '');
        this.detailsContainer.classList.remove('search-placeholder-changing');
        return;
      }

      this.detailsContainer.classList.add('search-placeholder-changing');

      if (isDeleting) {
        characterIndex = Math.max(0, characterIndex - 1);
      } else {
        characterIndex = Math.min(phrase.length, characterIndex + 1);
      }

      input.setAttribute('placeholder', phrase.slice(0, characterIndex));

      let delay = isDeleting ? 34 : 68;

      if (!isDeleting && characterIndex === phrase.length) {
        delay = 1080;
        isDeleting = true;
      } else if (isDeleting && characterIndex === 0) {
        this.mobileSearchPlaceholderIndex =
          (this.mobileSearchPlaceholderIndex + 1) % this.mobileSearchPlaceholders.length;
        this.detailsContainer.classList.remove('search-placeholder-changing');
        this.mobileSearchPlaceholderTimeout = window.setTimeout(() => {
          this.runMobileSearchPlaceholderTypewriter();
        }, 210);
        return;
      }

      this.mobileSearchPlaceholderTimeout = window.setTimeout(tick, delay);
    };

    this.mobileSearchPlaceholderTimeout = window.setTimeout(tick, 160);
  }

  stopMobileSearchPlaceholderAnimation() {
    if (this.mobileSearchPlaceholderInterval) {
      window.clearInterval(this.mobileSearchPlaceholderInterval);
      this.mobileSearchPlaceholderInterval = null;
    }
    if (this.mobileSearchPlaceholderTimeout) {
      window.clearTimeout(this.mobileSearchPlaceholderTimeout);
      this.mobileSearchPlaceholderTimeout = null;
    }
    this.mobileSearchPlaceholders = null;
    this.detailsContainer.classList.remove('search-placeholder-changing');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open') ? this.close() : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close(false);
  }

  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    event.target.closest('details').setAttribute('open', true);
    this.summaryToggle.setAttribute('aria-expanded', 'true');
    if (this.isMobileHeaderSearch()) {
      const headerOffset = this.headerWrapper?.getBoundingClientRect().top ?? 0;
      document.documentElement.style.setProperty('--sc-header-offset', `${Math.max(headerOffset, 0)}px`);
      this.updateMobileSearchViewport();
      this.onMobileSearchViewportChange = this.onMobileSearchViewportChange || this.updateMobileSearchViewport.bind(this);
      window.visualViewport?.addEventListener('resize', this.onMobileSearchViewportChange);
      window.visualViewport?.addEventListener('scroll', this.onMobileSearchViewportChange);
      window.addEventListener('resize', this.onMobileSearchViewportChange);
      this.startMobileSearchPlaceholderAnimation();
      this.headerWrapper?.classList.add('is-mobile-search-open');
      document.body.classList.add('mobile-search-open');
    }
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('overflow-hidden');

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close(focusToggle = true) {
    removeTrapFocus(focusToggle ? this.summaryToggle : null);
    this.detailsContainer.removeAttribute('open');
    this.summaryToggle.setAttribute('aria-expanded', 'false');
    if (this.isMobileHeaderSearch()) {
      window.visualViewport?.removeEventListener('resize', this.onMobileSearchViewportChange);
      window.visualViewport?.removeEventListener('scroll', this.onMobileSearchViewportChange);
      window.removeEventListener('resize', this.onMobileSearchViewportChange);
      document.documentElement.style.removeProperty('--sc-search-viewport-height');
      this.stopMobileSearchPlaceholderAnimation();
    }
    this.headerWrapper?.classList.remove('is-mobile-search-open');
    document.body.classList.remove('mobile-search-open');
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('overflow-hidden');
  }
}

customElements.define('details-modal', DetailsModal);
